import React, { useState } from 'react';
import {
  Popover,
  TextStyle,
  DisplayText,
  Modal,
  ResourceItem
} from '@superops/ui-design-system';
import {
  getTranslatedText,
  AccessControl,
  useActivityTracker
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import { getProcessedIntegrationOptions } from '@nile/components/src/Utils/integrationProcessing';
import { SVGIcon } from '@superops/super-icons';
import { RMMClient, INITIATE_REMOTE_SESSTION } from '@nile/graphql';
import { EVENT } from '@nile/components/src/Pages/SystemTrayChatPage/constants';

function ChatActionCell(props) {
  const { cell, intl, sendMessage } = props;

  const { original: asset } = cell?.row;

  const [remotePopoverActive, setRemotePopoverActive] = useState(false);
  const [screenshotConfirmation, setScreenshotConfirmation] = useState(false);

  const { configuredIntegrations, isAssetInClientPortal } = cell?.column;
  const SPLASHTOP_INTEGRATION_REFID = '1004';

  const splashTopWfh =
    configuredIntegrations?.find(
      (s) => s.integrationRefId === SPLASHTOP_INTEGRATION_REFID
    ) || {};
  const isEnableRequesterRemote =
    asset?.splashtopIntegrated &&
    asset?.client?.mappedToWFH &&
    !splashTopWfh?.ownLicense &&
    asset?.remoteDesktopEnabledStatus?.SUPEROPS_SPLASH_TOP &&
    splashTopWfh?.activated;

  const toggleRemotePopover = () => {
    setRemotePopoverActive(!remotePopoverActive);
  };

  const toggleScreenshotConfirmation = () => {
    setScreenshotConfirmation((prevState) => !prevState);
  };

  const getIconName = (name) => {
    switch (name) {
      case 'TEAM_VIEWER':
        return 'teamViewer';
      case 'SUPEROPS_SPLASH_TOP':
      case 'SPLASH_TOP':
        return 'Splashtop';
      case 'CONNECT_WISE_CONTROL':
        return 'connectWise';
      default:
        return name;
    }
  };

  const renderIntegrationOptions = (asset) => {
    const options = [
      {
        name: 'ScreenShot',
        label: getTranslatedText(intl, 'msp.chat.action.screen.capture'),
        screenShot: true
      }
    ];

    const remoteOption = getProcessedIntegrationOptions({
      configuredIntegrations,
      asset,
      intl
    });

    const handleOptionClick = (selectedOption, triggerToggle) => {
      if (selectedOption?.screenShot) {
        try {
          useActivityTracker({
            event: 'CHAT SCREENSHOT - TECHNICIAN'
          });
        } catch (err) {
          console.log(err);
        }
        toggleScreenshotConfirmation();

        return;
      }
      if (isAssetInClientPortal && !isEnableRequesterRemote) {
        return;
      }
      try {
        useActivityTracker({
          event: 'CHAT -  Remote Session initiated from chat'
        });
      } catch (err) {
        console.log(err);
      }
      RMMClient.mutate({
        mutation: INITIATE_REMOTE_SESSTION,
        variables: {
          assetId: asset.assetId,
          integrationType: selectedOption.name
        }
      })
        .then((res) => {
          sendMessage({
            event: EVENT.CONVERSATION_MESSAGE,
            conversation: {
              data: {
                type: EVENT.EVENT,
                action: EVENT.REMOTE_SESSION_STARTED
              }
            }
          });
          window.open(res.data.initiateRemoteSession, selectedOption.name);
          !triggerToggle && toggleRemotePopover();
        })
        .catch(() => {
          !triggerToggle && toggleRemotePopover();
        });
    };

    return (
      <>
        <Popover
          active={remotePopoverActive}
          activator={
            <div onClick={toggleRemotePopover}>
              <SVGIcon name="Actions" color="white" />
            </div>
          }
          onClose={toggleRemotePopover}
          customClassName="w280"
          preferredAlignment="left"
        >
          {options && options?.length > 0 && (
            <>
              <div className=" pt-5 pb-5 pl10 pr10">
                <DisplayText weight="Regular">
                  <span className="uppercase">
                    {getTranslatedText(
                      intl,
                      'admin.integrations.teamviewer.screenshots'
                    )}
                  </span>
                </DisplayText>
              </div>
              {options?.map((option: any, idx: Number) => (
                <ResourceItem
                  onClick={() => {
                    handleOptionClick(option);
                  }}
                  gutter="xs"
                  verticalAlignment="center"
                  key={idx}
                >
                  <div className="p10 flex actr g10">
                    <SVGIcon name={getIconName(option?.name)} />
                    <TextStyle weight="Regular">{option?.label}</TextStyle>
                  </div>
                </ResourceItem>
              ))}
            </>
          )}
          {remoteOption && remoteOption?.length > 0 && (
            <AccessControl
              roleToCheck="remoteDesktop.allow"
              render={
                <>
                  <div className=" pt-5 pb-5 pl10 pr10">
                    <DisplayText weight="Regular">
                      <span className="uppercase">
                        {getTranslatedText(
                          intl,
                          'admin.integrations.otherIntegrations'
                        )}
                      </span>
                    </DisplayText>
                  </div>
                  {remoteOption?.map((option: any, idx: Number) => (
                    <ResourceItem
                      onClick={() => {
                        handleOptionClick(option);
                      }}
                      gutter="xs"
                      verticalAlignment="center"
                      key={idx}
                    >
                      <div className="p10 flex actr g10">
                        <SVGIcon name={getIconName(option?.name)} />
                        <TextStyle weight="Regular">{option?.label}</TextStyle>
                      </div>
                    </ResourceItem>
                  ))}
                </>
              }
            />
          )}
        </Popover>
        <Modal
          open={screenshotConfirmation}
          iconSize="xl"
          onClose={() => toggleScreenshotConfirmation()}
          variant="confirmation"
          title={<FormattedMessage id="common.warning" />}
          primaryAction={{
            content: (
              <div className="flex actr ">
                <SVGIcon name="CircleTickOutlineMinor" />
                <div className="ml8">
                  <FormattedMessage id="common.yes" />
                </div>
              </div>
            ),
            onAction: () => {
              toggleScreenshotConfirmation();
              sendMessage({
                event: EVENT.CONVERSATION_MESSAGE,
                conversation: {
                  data: {
                    type: EVENT.EVENT,
                    action: EVENT.TECH_SCREENSHOT_REQUESTED
                  }
                }
              });
            }
          }}
          secondaryActions={[
            {
              content: <FormattedMessage id="common.cancel" />,
              onAction: () => toggleScreenshotConfirmation()
            }
          ]}
        >
          <div className="mt-10">
            <FormattedMessage id="msp.chat.technician.screenshot.request.confiramtion" />
          </div>
        </Modal>
      </>
    );
  };

  return (
    <div className="position-relative">{renderIntegrationOptions(asset)}</div>
  );
}

export default injectIntl(ChatActionCell);
